import { router } from "@inertiajs/react"
import { useEffect, useSyncExternalStore } from "react"

export type LiveStatus = {
  text: string | null
  emoji: string | null
  emoji_char: string | null
  busy: boolean
  starts: string | null
  expires: string | null
  // Set when the status on GitLab is one this app posted.
  status_id: number | null
}

export type GitlabStatusResponse = { status: LiveStatus | null; identity_added?: boolean } | { error: "token" | "unavailable" }

const REFRESH_AFTER_MS = 30_000

let response: GitlabStatusResponse | null = null
let fetchedAt = 0
let controller: AbortController | undefined
const listeners = new Set<() => void>()

function set(next: GitlabStatusResponse) {
  response = next
  listeners.forEach((listener) => listener())
}

export function loadGitlabStatus({ force = false } = {}) {
  if (!force && Date.now() - fetchedAt < REFRESH_AFTER_MS) return
  fetchedAt = Date.now()
  controller?.abort()
  controller = new AbortController()
  fetch("/gitlab_status", { headers: { Accept: "application/json" }, signal: controller.signal })
    .then((res) => (res.ok ? res.json() : { error: "unavailable" }))
    .then((next: GitlabStatusResponse) => {
      set(next)
      if ("identity_added" in next && next.identity_added) router.reload()
    })
    .catch(() => {})
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// Fetched after the page renders, and again on navigation, so a slow GitLab never holds up a page.
export function useGitlabStatus(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    loadGitlabStatus()
    return router.on("navigate", () => loadGitlabStatus())
  }, [enabled])

  return useSyncExternalStore(
    subscribe,
    () => response,
    () => null,
  )
}
